import api from '../../services/api';
import { ResponseData } from '../../types/response/ResponseData';
import ResponseError from '../../types/response/ResponseError';
import { Merchant } from '../../types/onboarding/Merchant';
import {
    emptyPriceList,
    MerchantPriceList,
    StorePriceSyncResult,
} from '../../types/onboarding/MerchantPrices';
import { ONBOARDING_REQUEST_TIMEOUT_IN_MILLISECONDS } from './onboarding';

const merchantPricesPath = (organizationId: string, merchant: Merchant) =>
    `organizations/${organizationId}/merchants/${encodeURIComponent(
        merchant.countryCode
    )}/${encodeURIComponent(merchant.organizationNumber)}/prices`;

/**
 * Gets the price list of a merchant (GET organizations/{id}/merchants/{countryCode}/{organizationNumber}/prices).
 * A merchant without prices gets an empty list. Requires read on the organization, like listing the merchants.
 */
export const getMerchantPrices = async (
    organizationId: string,
    merchant: Merchant,
    getPricesStarted: (merchant: Merchant) => void,
    getPricesSucceeded: (merchant: Merchant, priceList: MerchantPriceList) => void,
    getPricesFailed: (error: ResponseError) => void
) => {
    getPricesStarted(merchant);

    try {
        const response = await api.get<ResponseData<MerchantPriceList>>(
            merchantPricesPath(organizationId, merchant)
        );

        getPricesSucceeded(merchant, response.data ?? emptyPriceList);
    } catch (error) {
        getPricesFailed(error as ResponseError);
    }
};

/**
 * Replaces the price list of a merchant (PUT organizations/{id}/merchants/{countryCode}/{organizationNumber}/prices).
 * Nothing is pushed to Edge until the list is synced. Requires write on the organization; the UI only offers it to
 * internal users.
 */
export const saveMerchantPrices = async (
    organizationId: string,
    merchant: Merchant,
    priceList: MerchantPriceList,
    savePricesStarted: () => void,
    savePricesSucceeded: (priceList: MerchantPriceList) => void,
    savePricesFailed: (error: ResponseError) => void
) => {
    savePricesStarted();

    try {
        const response = (await api.put(
            merchantPricesPath(organizationId, merchant),
            priceList
        )) as ResponseData<MerchantPriceList> | undefined;

        savePricesSucceeded(response?.data ?? priceList);
    } catch (error) {
        savePricesFailed(error as ResponseError);
    }
};

/**
 * Makes Edge reflect the stored price list of a merchant in every store of the merchant
 * (POST organizations/{id}/merchants/{countryCode}/{organizationNumber}/prices/sync). Edge failures per store or product
 * come back in the result, not as an error. Not retried (the sync creates settlement products) and given the longer
 * timeout since every store is visited.
 */
export const syncMerchantPrices = async (
    organizationId: string,
    merchant: Merchant,
    syncPricesStarted: () => void,
    syncPricesSucceeded: (results: StorePriceSyncResult[]) => void,
    syncPricesFailed: (error: ResponseError) => void
) => {
    syncPricesStarted();

    try {
        const response = await api.postWithResponseType<
            Record<string, never>,
            ResponseData<StorePriceSyncResult[]>
        >(`${merchantPricesPath(organizationId, merchant)}/sync`, {}, undefined, {
            timeout: ONBOARDING_REQUEST_TIMEOUT_IN_MILLISECONDS,
            retries: 0,
        });

        syncPricesSucceeded(response.data ?? []);
    } catch (error) {
        syncPricesFailed(error as ResponseError);
    }
};
